'use client';

import { capitalCase } from 'change-case';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { useEffect, useMemo, useState, type KeyboardEvent, type MouseEvent } from 'react';

import { FeaturePageBleed } from './FeaturePageShell';
import Pagination, { usePagination } from './Pagination';
import Button from '~/components/Button';
import { tableAriaSort, TableSortIcon } from '~/components/TableSortControl';
import { markPendingNavigation } from '~/components/Navigation/routeNavigationStore';
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Table as UiTable,
} from '~/components/ui/table';
import { cn, interactiveHoverClassName } from '~/lib/utils';

export type OrderBy<T extends object> = {
  key: keyof T;
  order: 'ASC' | 'DESC';
};

export type TableProps<T extends Record<string, any>> = {
  data: T[];
  columns: Column<T>[];
  onRowClick?: (row: T, e: MouseEvent<HTMLTableRowElement, globalThis.MouseEvent>) => void;
  activeRowId?: string;
  pageSize?: number;
  orderBy?: { key: keyof T; order: 'ASC' | 'DESC' } | undefined;
  sortData?: (key: keyof T, order: 'ASC' | 'DESC', data: T[]) => T[] | undefined | void;
  onSortChange?: (order: OrderBy<T> | null) => void;
  className?: string;
  rowHref?: (row: T) => string;
  thTdClassName?: string;
  trClassName?: string;
  tableClassName?: string;
  stickyFirstColumn?: boolean;
  /** `card` (default) wraps the table; `plain` is flush like Policy Engine lists. */
  variant?: 'card' | 'plain';
  /** Let cell popovers escape the card instead of clipping to rounded overflow. */
  allowOverflow?: boolean;
  /**
   * Plain tables inside FeaturePageShell: rest-state gutters align with the
   * reading column; horizontal overflow clips at the shell. Default on for
   * `plain`. Set false in overlays / nested cards.
   */
  bleed?: boolean;
  rowClassName?: string | ((row: T) => string);
};

export type Column<T> = {
  /** Unique ID for the React `key` prop in list elements to make sure rows are rendered in the correct order */
  id?: string;
  key?: keyof T;
  title?: React.ReactNode;
  render?: React.ReactNode | ((row: T) => React.ReactNode);
  sortable?: boolean;
  width?: string | number;
  minWidth?: string | number;
  href?: (row: T) => string;
  tdClassName?: string;
  thClassName?: string;
  thStyle?: React.CSSProperties;
  tdStyle?: React.CSSProperties;
};

export default function Table<T extends Record<string, any>>({
  data,
  columns,
  onRowClick,
  activeRowId,
  pageSize,
  sortData,
  className,
  orderBy,
  rowHref,
  onSortChange,
  thTdClassName,
  trClassName,
  tableClassName,
  stickyFirstColumn,
  variant = 'card',
  bleed,
  rowClassName,
  allowOverflow,
}: TableProps<T>) {
  const { currentPage, setCurrentPage } = usePagination(1);
  const [sorting, setSorting] = useState<OrderBy<T> | null>(null);
  const router = useRouter();
  const pathname = usePathname();

  const isModifiedClick = (e: { metaKey?: boolean; ctrlKey?: boolean; button?: number }) =>
    Boolean(e.metaKey || e.ctrlKey || e.button === 1);

  const openRowHref = (row: T, e: { metaKey?: boolean; ctrlKey?: boolean; button?: number }) => {
    const href = rowHref?.(row);
    if (!href) return;
    if (isModifiedClick(e)) {
      window.open(href, '_blank', 'noopener,noreferrer');
      return;
    }
    markPendingNavigation(href, pathname);
    router.push(href);
  };

  const isNestedControl = (target: EventTarget | null) =>
    Boolean(
      (target as HTMLElement | null)?.closest(
        'button, a, input, select, textarea, [role="menu"], [role="combobox"]',
      ),
    );

  useEffect(() => {
    if (
      orderBy !== undefined &&
      (orderBy?.key !== sorting?.key || orderBy?.order !== sorting?.order)
    ) {
      setSorting(orderBy);
    }
  }, [orderBy]);

  const sortedData = useMemo(() => {
    const sorted = sorting && sortData ? sortData(sorting.key, sorting.order, data) : data;
    if (!sorted) return data;
    return typeof pageSize === 'number'
      ? sorted.slice((currentPage - 1) * pageSize, currentPage * pageSize)
      : sorted;
  }, [data, sorting, currentPage, pageSize]);

  const columnsWithID = columns.map((c, idx) => ({ id: idx.toString(), ...c }));
  const isInteractive = Boolean(onRowClick || rowHref);
  const isPlain = variant === 'plain';
  const shouldBleed = isPlain && (bleed ?? true);
  const tableClassNameResolved = cn(
    isPlain && 'min-w-[640px] border-collapse',
    stickyFirstColumn && 'table-pin-cols',
    tableClassName,
  );

  const tableInner = (
    <>
      <TableHeader className="[&_tr]:border-0">
        <TableRow className="hover:bg-transparent">
          {columnsWithID.map((c, idx) => (
            <TableHead
              key={c.id || idx.toString()}
              aria-sort={
                c.sortable && c.key
                  ? tableAriaSort(sorting?.key === c.key ? sorting.order : null)
                  : undefined
              }
              className={cn(
                'text-muted-foreground h-auto overflow-hidden text-[13px] font-medium',
                isPlain ? 'px-2 pt-1 pb-3' : 'px-3 pt-2.5 pb-3',
                idx === 0 && stickyFirstColumn && 'bg-card sticky left-0 z-10',
                c.sortable && c.key && 'cursor-pointer select-none',
                c.thClassName,
                thTdClassName,
              )}
              style={{
                width: c.width,
                minWidth: c.minWidth,
                whiteSpace: 'normal',
                wordWrap: 'break-word',
                ...c.thStyle,
              }}
              onClick={() => {
                if (!c.key || !c.sortable) return;
                setSorting((prev) => {
                  let newValue = prev ? { ...prev } : prev;
                  if (!c.key) {
                  } else if (prev && prev?.key === c.key) {
                    newValue = {
                      key: c.key,
                      order: prev.order === 'ASC' ? 'DESC' : 'ASC',
                    };
                  } else {
                    newValue = { key: c.key, order: 'ASC' };
                  }

                  onSortChange?.(newValue);

                  return newValue;
                });
              }}
            >
              <div
                className={cn(
                  'flex min-w-0 items-center gap-1 transition-colors',
                  c.sortable && 'cursor-pointer',
                  c.sortable && c.key && sorting?.key === c.key && 'text-foreground',
                )}
              >
                {c.title || (c.key ? capitalCase(c.key as string) : '')}
                {c.key && c.sortable ? (
                  <TableSortIcon order={sorting?.key === c.key ? sorting.order : null} />
                ) : null}
              </div>
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {sortedData.map((row, i) => (
          <TableRow
            key={row.id || i.toString()}
            data-state={!!row.id && activeRowId === row.id ? 'selected' : undefined}
            role={rowHref ? 'link' : undefined}
            tabIndex={rowHref ? 0 : undefined}
            aria-label={rowHref ? 'Open' : undefined}
            className={cn(
              isPlain ? 'border-border/50 border-t border-b-0' : 'border-border/50',
              isInteractive && cn('cursor-pointer', interactiveHoverClassName),
              !isInteractive && 'hover:bg-transparent',
              rowHref &&
                'focus-visible:ring-ring/50 focus-visible:ring-2 focus-visible:outline-none',
              trClassName,
              typeof rowClassName === 'function' ? rowClassName(row) : rowClassName,
            )}
            onClick={(e: MouseEvent<HTMLTableRowElement>) => {
              if (isNestedControl(e.target)) return;
              if (rowHref) {
                openRowHref(row, e);
                return;
              }
              onRowClick?.(row, e);
            }}
            onAuxClick={(e: MouseEvent<HTMLTableRowElement>) => {
              if (e.button !== 1 || !rowHref || isNestedControl(e.target)) return;
              e.preventDefault();
              openRowHref(row, e);
            }}
            onKeyDown={(e: KeyboardEvent<HTMLTableRowElement>) => {
              if (!rowHref || (e.key !== 'Enter' && e.key !== ' ')) return;
              if (isNestedControl(e.target)) return;
              e.preventDefault();
              openRowHref(row, e);
            }}
          >
            {columnsWithID.map((c, idx) => {
              const renderRow =
                typeof c.render === 'function'
                  ? c.render(row)
                  : c.render || (!c.key || typeof row[c.key] === 'object' ? '' : row[c.key]);
              const cellHref = c.href?.(row);

              return (
                <TableCell
                  key={c.id || c.key?.toString() || i.toString()}
                  style={{
                    width: c.width,
                    minWidth: c.minWidth,
                    ...c.tdStyle,
                  }}
                  className={cn(
                    'text-foreground min-w-0 align-middle',
                    isPlain ? 'overflow-hidden px-2 py-3.5' : 'px-3 py-3.5',
                    idx === 0 && stickyFirstColumn && 'bg-card sticky left-0 z-10',
                    c.tdClassName,
                    thTdClassName,
                  )}
                >
                  {cellHref ? (
                    <Button variant="link" asChild className="h-auto max-w-full p-0">
                      <Link href={cellHref}>{renderRow}</Link>
                    </Button>
                  ) : (
                    renderRow
                  )}
                </TableCell>
              );
            })}
          </TableRow>
        ))}
      </TableBody>
    </>
  );

  const table = isPlain ? (
    <table className={cn('w-full caption-bottom text-sm', tableClassNameResolved)}>
      {tableInner}
    </table>
  ) : (
    <UiTable
      className={tableClassNameResolved}
      wrapperClassName={allowOverflow ? 'overflow-visible' : undefined}
    >
      {tableInner}
    </UiTable>
  );

  return (
    <div className="flex flex-col gap-3">
      {shouldBleed ? (
        <FeaturePageBleed className={className}>{table}</FeaturePageBleed>
      ) : (
        <div
          className={cn(
            isPlain
              ? 'w-full overflow-x-auto'
              : cn(
                  'border-border bg-card rounded-xl border shadow-[0_1px_2px_rgba(0,0,0,0.04)]',
                  allowOverflow ? 'overflow-visible' : 'overflow-hidden',
                ),
            className,
          )}
        >
          {table}
        </div>
      )}
      {typeof pageSize === 'number' && (
        <Pagination
          dense
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          total={Math.ceil(data.length / pageSize)}
          itemsPerPage={pageSize}
          totalItems={data.length}
          rangeOnly
        />
      )}
    </div>
  );
}
